import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Link2, UserCheck } from 'lucide-react';
import type { OrgMember } from '../../../hooks/useOrgMembers';
import { resolveDirectManager } from '../services/lstHierarchy';
import { LST_CARGO_LABELS, mapRoleTypeToCargo } from '../types';

const inputCls =
  'h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none transition-colors focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200';
const labelCls = 'mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400';

interface CadeiaAjudaConfigProps {
  members: OrgMember[];
  currentUserId: string;
  isAdmin: boolean;
  savingLink: string | null;
  onSetReportsTo: (userId: string, reportsToUserId: string | null) => Promise<boolean>;
  /** Sem borda/card externo — embutido no painel direito de Configuração. */
  embedded?: boolean;
}

/**
 * Cadeia de Ajuda — gestor direto herdado da hierarquia (não obrigatório).
 * Override apenas para exceções.
 */
export const CadeiaAjudaConfig: React.FC<CadeiaAjudaConfigProps> = ({
  members,
  currentUserId,
  isAdmin,
  savingLink,
  onSetReportsTo,
  embedded = false,
}) => {
  const [showExceptions, setShowExceptions] = useState(false);
  const [exceptionSearch, setExceptionSearch] = useState('');
  const [overlay, setOverlay] = useState<Record<string, string | null>>({});

  const me = useMemo(() => members.find((m) => m.id === currentUserId) ?? null, [members, currentUserId]);
  const inferredManager = useMemo(() => resolveDirectManager(me, members), [me, members]);

  const effectiveManagerId = useMemo(() => {
    if (!me) return '';
    if (me.id in overlay) return overlay[me.id] ?? '';
    return me.reportsToUserId ?? inferredManager?.id ?? '';
  }, [me, inferredManager, overlay]);

  const effectiveManagerName = useMemo(() => {
    if (!effectiveManagerId) return null;
    return members.find((m) => m.id === effectiveManagerId)?.name ?? null;
  }, [effectiveManagerId, members]);

  const managerOptions = useMemo(
    () => members.filter((m) => m.id !== currentUserId),
    [members, currentUserId],
  );

  const handleMyManagerChange = async (value: string) => {
    const reportsTo = value === '' ? null : value;
    setOverlay((prev) => ({ ...prev, [currentUserId]: reportsTo }));
    const ok = await onSetReportsTo(currentUserId, reportsTo);
    if (!ok) {
      setOverlay((prev) => {
        const next = { ...prev };
        delete next[currentUserId];
        return next;
      });
    }
  };

  const handleExceptionChange = async (userId: string, value: string) => {
    const reportsTo = value === '' ? null : value;
    setOverlay((prev) => ({ ...prev, [userId]: reportsTo }));
    const ok = await onSetReportsTo(userId, reportsTo);
    if (!ok) {
      setOverlay((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  };

  const exceptionList = useMemo(() => {
    const q = exceptionSearch.trim().toLowerCase();
    return members
      .filter((m) => m.id !== currentUserId)
      .filter((m) => !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [members, currentUserId, exceptionSearch]);

  const body = (
    <>
      <div className={`shrink-0 space-y-2 ${embedded ? 'p-3' : 'border-b border-slate-100 p-3 dark:border-slate-800'}`}>
        <div className="rounded-md border border-emerald-100 bg-emerald-50/50 px-3 py-2 dark:border-emerald-900/40 dark:bg-emerald-900/10">
          <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
            <UserCheck size={12} /> Seu gestor direto
          </div>
          {effectiveManagerName ? (
            <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-100">{effectiveManagerName}</p>
          ) : (
            <p className="text-[11px] italic text-slate-500">Nenhum gestor identificado na hierarquia — opcional.</p>
          )}
          {!me?.reportsToUserId && inferredManager && (
            <p className="mt-0.5 text-[9px] text-slate-400">Inferido automaticamente pelo setor/cargo.</p>
          )}
        </div>

        <div>
          <label className={labelCls}>Exceção (opcional)</label>
          <select
            className={inputCls}
            value={effectiveManagerId}
            disabled={savingLink === currentUserId}
            onChange={(e) => handleMyManagerChange(e.target.value)}
          >
            <option value="">— Sem gestor / usar hierarquia —</option>
            {managerOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
      </div>

      {isAdmin && (
        <div className="min-h-0 flex-1">
          <button
            type="button"
            onClick={() => setShowExceptions((v) => !v)}
            className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-900/30"
          >
            {showExceptions ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Ajustes excepcionais por usuário
          </button>
          {showExceptions && (
            <>
              <div className="border-b border-slate-100 p-2 dark:border-slate-800">
                <input
                  className={inputCls}
                  value={exceptionSearch}
                  onChange={(e) => setExceptionSearch(e.target.value)}
                  placeholder="Buscar usuário…"
                />
              </div>
              <div className={embedded ? 'max-h-none' : 'max-h-[220px] overflow-y-auto'}>
                {exceptionList.map((m) => {
                  const inferred = resolveDirectManager(m, members);
                  const current = m.id in overlay ? overlay[m.id] : m.reportsToUserId ?? inferred?.id ?? '';
                  return (
                    <div key={m.id} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0 dark:border-slate-800/60">
                      <div className="min-w-0 flex-1">
                        <span className="block truncate text-[12px] font-semibold text-slate-800 dark:text-slate-100">{m.name}</span>
                        <span className="text-[10px] text-slate-400">{LST_CARGO_LABELS[mapRoleTypeToCargo(m.roleType)]}</span>
                      </div>
                      <select
                        className={`${inputCls} w-40`}
                        value={current ?? ''}
                        disabled={savingLink === m.id}
                        onChange={(e) => handleExceptionChange(m.id, e.target.value)}
                      >
                        <option value="">— Hierarquia —</option>
                        {members.filter((o) => o.id !== m.id).map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div className="flex min-h-0 flex-col">{body}</div>;
  }

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-[var(--bg-surface)]">
      <header className="shrink-0 border-b border-slate-100 px-4 py-2.5 dark:border-slate-800">
        <h3 className="flex items-center gap-2 text-[13px] font-bold text-slate-800 dark:text-slate-100">
          <Link2 size={15} className="text-blue-500" /> Cadeia de Ajuda
        </h3>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Gestor direto preenchido pela hierarquia da organização. Altere só em exceção.
        </p>
      </header>
      {body}
    </section>
  );
};

export default CadeiaAjudaConfig;
